// The Experience content model.
//
// This is the single source of truth for both the public catalog/detail pages
// and the admin CMS. It mirrors the fields the original static site stored in
// `localStorage['experiencesDB']`, plus the richer fields exposed by the admin
// "Add New Experience" form (booking type, guests, gallery, includes, etc.).
//
import type { ClosurePeriodType, WeekdayClosureType } from './types_closure';
import type { HistoryInfoType } from './types_history';

// Firestore collection name (for when the backend is wired up).
export const coll_experiences = 'experiences';

// Archived edit-history steps, written by the `experiences_written` Cloud
// Function. The name is derived server-side as `{collection} + 'history'`, so
// the two must be kept in step.
export const coll_experiencesHistory = coll_experiences + 'history';

export type ExperienceRegionType =
  | 'kanto'
  | 'kansai'
  | 'hokkaido'
  | 'kyushu'
  | 'okinawa'
  | 'tohoku'
  | 'chubu'
  | 'chugoku'
  | 'shikoku';

// Key order here is the canonical display order for region pickers and the
// public catalog filter — Okinawa sits last, after the mainland regions.
export const experienceRegionLabels: Record<ExperienceRegionType, string> = {
  kanto: 'Kanto (Tokyo Area)',
  kansai: 'Kansai (Kyoto/Osaka)',
  hokkaido: 'Hokkaido',
  kyushu: 'Kyushu',
  tohoku: 'Tohoku',
  chubu: 'Chubu',
  chugoku: 'Chugoku',
  shikoku: 'Shikoku',
  okinawa: 'Okinawa',
};

export const experienceRegionOrder = Object.keys(experienceRegionLabels) as ExperienceRegionType[];

/**
 * The region and the venue on one line, for the subtitle of a card or a row.
 *
 * Joined rather than interpolated because `venueName` is optional: an experience
 * with no venue typed into it reads as its region alone, not as a region with a
 * dangling separator after it.
 */
export function experienceRegionVenueLine(
  experience: Pick<ExperienceType, 'region' | 'venueName'>,
): string {
  return [experienceRegionLabels[experience.region], experience.venueName.trim()]
    .filter(Boolean)
    .join(' · ');
}

// Controls how the public detail page renders the hero/body.
export type ExperienceLayoutType = 'standard' | 'gallery-focus' | 'video-hero' | 'minimal';

export const experienceLayoutLabels: Record<ExperienceLayoutType, string> = {
  standard: 'Standard',
  'gallery-focus': 'Gallery Focus',
  'video-hero': 'Video Hero',
  minimal: 'Minimal',
};

/**
 * How a request for this experience is worked, which decides two things at once:
 * whether a date can be held while the partner considers it, and therefore whether
 * the booking can ever be cancelled free of charge.
 *
 * 'inquiry-immediate' is the geisha case. There is no holding a date — the call
 * is answered yes or no while the admin is on the phone — so the booking is firm
 * from the moment it is taken, and the supplier is owed for it from that moment
 * too. The two properties are deliberately one setting rather than two: an
 * experience that cannot be held is one that cannot be released, and letting an
 * admin set those independently would only ever produce a wrong combination.
 */
export type ExperienceBookingType = 'direct' | 'inquiry' | 'inquiry-immediate';

export const experienceBookingTypeLabels: Record<ExperienceBookingType, string> = {
  direct: 'Direct Booking (with calendar)',
  inquiry: 'Inquiry Only',
  'inquiry-immediate': 'Inquiry — immediate yes/no, non-refundable',
};

/**
 * Whether a booking for this experience is firm the instant it is confirmed.
 *
 * One predicate rather than a comparison spelled out at each site, because it is
 * asked in three places that must never disagree: the cancellation window
 * snapshotted at confirmation, the wording of the confirmation email, and what
 * the admin is asked to acknowledge before they pick up the phone.
 */
export function isNonRefundableBookingType(bookingType: ExperienceBookingType): boolean {
  return bookingType === 'inquiry-immediate';
}

/**
 * Whether this experience is booked off a calendar of availability slots rather
 * than by inquiry.
 *
 * The one predicate the whole feature turns on, asked in five places that must
 * agree: which panel the public page renders, whether the CMS offers a calendar
 * to fill in, which half of `validateBookingRequest` applies, whether
 * `createBookingRequest` takes a slot and confirms on the spot, and whether
 * cancelling puts a date back on the calendar.
 *
 * A direct booking is the case where the artisan has *already* said yes: a slot
 * exists because somebody agreed to work it, so the concierge who takes it has
 * the booking, and there is nothing for us to hold or decide. That is also why
 * it is not the same question as `isNonRefundableBookingType` — an immediate
 * inquiry is answered on the spot too, but by a phone call nobody made in
 * advance, and it is firm from that moment because of it.
 */
export function isCalendarBookingType(bookingType: ExperienceBookingType): boolean {
  return bookingType === 'direct';
}

// --- Optional services ---

/**
 * Something a partner may ask for alongside the experience itself — a private
 * shuttle, an interpreter, a lunch afterwards.
 *
 * Configured per experience because the list belongs to the artisan rather than
 * to the catalogue: a workshop an hour outside Kyoto is worth offering a car
 * with, a tea house across the road from the guest's lodgings is not. Nothing is
 * ever chosen for the concierge — the inquiry form ticks none of them, so a
 * service on a booking is one somebody asked for.
 *
 * A selected service becomes a `BookingPriceLineType` on the booking, which is
 * why the two share their shape: this is a price line the partner can add itself,
 * rather than one the desk types out on the phone.
 */
export interface ExperienceOptionalServiceType {
  /**
   * Stable, minted once when the row is added, and what a booking request names.
   *
   * The label cannot be the identity: a booking snapshots the service it was
   * made with, and a service renamed or repriced afterwards must leave those
   * bookings pointing at the row they were actually sold.
   */
  id: string;
  label: string;
  /**
   * JPY, tax-inclusive and whole yen, for the booking as a whole rather than per
   * guest — one shuttle carries the party, and it is the same reading as a
   * direct booking's own price. Where a service really is per head, price the
   * party and say so in the label ("Interpreter, half day").
   */
  price: number;
  /**
   * Whether the partner earns its commission on this service. Off for a new row,
   * because an extra we arrange at cost is what this list is mostly for — see
   * `BookingPriceLineType`, where the same flag decides the same thing.
   */
  commissionable: boolean;
  /**
   * Where the row sits in the list the partner is shown. Minted as one past the
   * highest in use and then never touched again — deleting a row leaves a gap
   * rather than renumbering its neighbours, so no edit to one service ever
   * writes to another. That is the whole point of the map: a gap costs nothing,
   * and a renumber would put every row back in play on every delete.
   */
  order: number;
}

/**
 * How many services one experience may offer, and how long a label may be.
 *
 * Deliberately one under `bookingPriceMaxLines`: a booking that ticks every
 * service still has room for the experience's own row, so a concierge can never
 * assemble a booking the offer form cannot then hold.
 */
export const experienceMaxOptionalServices = 9;
export const optionalServiceLabelMaxLength = 80;

/**
 * A new, empty service row at the end of the list. The id is minted here and
 * never changes after; `order` comes from `nextOptionalServiceOrder`, which is
 * the only thing that ever chooses one.
 */
export function makeOptionalService(order: number): ExperienceOptionalServiceType {
  return { id: crypto.randomUUID(), label: '', price: 0, commissionable: false, order };
}

/**
 * The services of one experience in the order they are offered.
 *
 * Stored as a map keyed by service id so a row can be repriced with a write to
 * `optionalServices.{id}.price` alone — see `ExperienceType.optionalServices`.
 * Every reader wants the list, though, so they all come through here.
 */
export function normalizeOptionalServices(
  services: Record<string, ExperienceOptionalServiceType>,
): ExperienceOptionalServiceType[] {
  // By id after order, so two rows that somehow share a position still come out
  // in the same sequence on every reader rather than at Object.values' whim.
  return Object.values(services).sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));
}

/** The position a row added now would take. */
export function nextOptionalServiceOrder(services: ExperienceOptionalServiceType[]): number {
  return services.reduce((max, service) => Math.max(max, service.order), 0) + 1;
}

/**
 * The services actually offered: labels trimmed, rows nobody filled in dropped.
 *
 * A row counts as unused only when it has neither a label nor a price, which is
 * what a row the admin added and then thought better of looks like. A labelled
 * row at ¥0 is kept — a service we arrange and do not charge for is worth
 * offering, and dropping it would quietly remove it from the form.
 */
export function cleanOptionalServices(
  services: ExperienceOptionalServiceType[],
): ExperienceOptionalServiceType[] {
  return services
    .map((service) => ({ ...service, label: service.label.trim() }))
    .filter((service) => service.label || service.price);
}

/**
 * Why a set of services cannot be saved, or null when it can. Call it on cleaned
 * rows.
 */
export function validateOptionalServices(services: ExperienceOptionalServiceType[]): string | null {
  if (services.length > experienceMaxOptionalServices) {
    return `An experience can offer no more than ${experienceMaxOptionalServices} optional services.`;
  }
  if (services.some((service) => !service.label)) return 'Every optional service needs a name.';
  if (services.some((service) => service.label.length > optionalServiceLabelMaxLength)) {
    return 'One of the service names is longer than we can store.';
  }
  if (services.some((service) => !Number.isInteger(service.price) || service.price < 0)) {
    return 'Every optional service price must be a whole number of yen.';
  }
  return null;
}

/**
 * The services these ids name, and the ids that name nothing.
 *
 * In the experience's own order rather than the order they were ticked, so one
 * booking's services read the same however they were chosen — and so the
 * snapshot written onto a request matches the list the concierge saw.
 *
 * `unknown` is what a stale page produces: a service deleted from the experience
 * after the form was loaded. It is reported rather than quietly dropped, since
 * the alternative is a partner being sent a booking without the shuttle they asked
 * for and no indication that anything went missing.
 */
export function resolveOptionalServices(
  offered: ExperienceOptionalServiceType[],
  ids: string[],
): { services: ExperienceOptionalServiceType[]; unknown: string[] } {
  const wanted = new Set(ids);
  return {
    services: offered.filter((service) => wanted.has(service.id)),
    unknown: ids.filter((id) => !offered.some((service) => service.id === id)),
  };
}

export type ExperienceStatusType = 'active' | 'inactive' | 'coming-soon';

export const experienceStatusLabels: Record<ExperienceStatusType, string> = {
  active: 'Active',
  inactive: 'Inactive',
  'coming-soon': 'Coming Soon',
};

export interface ExperienceType {
  id: string;
  /**
   * The two characters that stand for this experience inside a tour request
   * code — `UK` for Uchikake, in `X68E-UKAMS6`. Unique across the catalogue, or
   * '' for an experience that has not been given one yet (every experience
   * predating the field).
   *
   * Unique because a request code is read backwards: given a reference, the
   * desk has to be able to say which experience it names, and two experiences
   * sharing `UK` would make that answer ambiguous. Held in uppercase, and
   * compared that way, so `uk` and `UK` are one code rather than two — which is
   * also what makes the uniqueness check a single equality query, see
   * `isExperienceCodeTaken`.
   *
   * Not the document id. The id is the slug the public URL is built from and
   * can never change once bookings point at it; a code is a label the desk
   * chooses. Re-cutting one leaves existing request codes pointing at the old
   * two characters, so it is not a free change either.
   */
  code: string;
  name: string;
  subtitle: string;
  region: ExperienceRegionType;

  /**
   * The place the experience is run, as it should be named to a guest — "Kaikaro
   * Teahouse", "Studio Kotoha". Optional: plenty of the catalogue is a walking
   * tour or a pickup rather than a building, and a made-up venue name is worse
   * than none.
   *
   * This is what the UI shows wherever the old `location` was shown, so it
   * carries that value forward for experiences written before the field existed
   * — see `normaliseExperience`.
   */
  venueName: string;
  /**
   * The street address, written the way it would be shown to a taxi driver.
   * Optional, and printed on the guest's confirmation sheet when set.
   *
   * Multi-line: edited in a three-row textarea and printed one line per line, as
   * an address is written on an envelope. PDFKit breaks on the newlines itself,
   * in `heightOfString` as well as `text`, so the row on the sheet grows to fit
   * — nothing splits the string by hand.
   */
  venueAddress: string;
  /**
   * A Google Maps link to the venue (the "Share" link, not the embed — that is
   * `googleMapsEmbedUrl`). When set, the address on the confirmation PDF is
   * printed as a link to it, so a guest holding the sheet on a phone gets
   * directions rather than a string to retype.
   */
  venueMapUrl: string;

  /**
   * Legacy "Location / City" — "Kyoto", "Tokyo - Toyosu". Replaced by
   * `venueName`, and no longer editable or read anywhere: it stays on the type
   * so the value stored against every pre-venue experience is still there to be
   * carried forward, which `normaliseExperience` does.
   */
  location: string;

  layoutFormat: ExperienceLayoutType;

  // Pricing. basePrice is in JPY; 0 means "price on request".
  basePrice: number;
  bookingType: ExperienceBookingType;
  minGuests: number | null;
  maxGuests: number | null;

  // Commission for partner bookings.
  commissionEnabled: boolean;
  commissionPercent: number;

  // How many days before the booked day a partner may still cancel without
  // charge. Per experience because the artisans differ: a tea ceremony can
  // release a morning at short notice, a kimono dresser who has blocked a day
  // cannot.
  //
  // Whole days rather than hours because that is how the policy is agreed with
  // the artisan and read by a concierge: "seven days before" is a date on a
  // calendar, not an hour of the morning. It counts back from midnight
  // beginning the booked day, so seven means seven clear days of notice and two
  // slots on the same date share one deadline — see
  // `computeFreeCancellationUntil` in types_bookingRequest.ts.
  //
  // Snapshotted onto a booking when it is confirmed, so changing this never
  // alters what an existing booking agreed to.
  freeCancellationDays: number;

  // Whether a familiarisation (FAM) trip is available for partners.
  famEnabled: boolean;

  // Extras a partner may tick when they ask for this experience — a private
  // shuttle, an interpreter. Empty for most experiences, and never chosen by
  // default; see `ExperienceOptionalServiceType`.
  //
  // A map rather than a list so that repricing a shuttle writes
  // `optionalServices.{id}.price` and nothing else: two admins on the same
  // experience no longer overwrite each other's rows, and a slow connection
  // sends one field instead of the whole catalogue. Read it through
  // `normalizeOptionalServices`, which puts it back in order.
  optionalServices: Record<string, ExperienceOptionalServiceType>;

  // Content.
  shortDescription: string;
  // Rich text: HTML written in the admin's `RichTextField`, or plain text for
  // records predating that. Render with `<RichText>`, never as a bare string.
  fullDescription: string;
  includes: string[];

  // A short line set large and italic at the end of the description, lifted out
  // of the body copy (e.g. "This is not a tourist performance…"). Optional.
  closingStatement: string;

  // Media.
  heroImage: string;
  // A YouTube link (any of the watch/youtu.be/embed/shorts forms), used as the
  // hero background under the 'video-hero' layout. Nothing else is supported.
  videoUrl: string;
  galleryImages: string[];
  googleMapsEmbedUrl: string;

  // Who runs this experience (`suppliers/{id}`), or null for one with no supplier
  // record — most of the catalogue, until somebody links them up.
  //
  // The link is worth having for one thing today: the supplier holds the days
  // the artisan is away in one place, and every experience pointing at it
  // inherits them, so a tea house closing for a week is typed once rather than
  // into each of its ten experiences. Nothing else on the experience is read
  // from the supplier, and an experience without one behaves exactly as it did.
  //
  // Not a promise the database keeps: a supplier can be deleted out from under
  // this (the CMS refuses while experiences point at it, but a direct write
  // could), and every reader treats a supplier it cannot find as no supplier —
  // no inherited absences, nothing else changed.
  supplierId: string | null;

  // Days this experience cannot be run on, whatever its booking type — the
  // artisan is away, or the place it depends on is shut that day of the week.
  // See types_closure.ts; together these two fields satisfy
  // `ExperienceClosuresType`, which is what every reader takes.
  closurePeriods: ClosurePeriodType[];
  weekdayClosures: WeekdayClosureType[];

  status: ExperienceStatusType;

  // Collection this experience belongs to, or null for a standalone experience.
  collectionId: string | null;

  // Position in the home page's "Featured Experiences" section (lower shows first),
  // or null to leave this experience out of that section entirely.
  featuredOrder: number | null;

  // Legacy pointer to the original static HTML page, kept for reference during
  // the migration. Not used for routing (routes are driven by `id`).
  legacyPageUrl?: string;

  // ISO timestamps. Optional until the Firestore backend populates them.
  createdAt?: string;
  updatedAt?: string;

  // Persisted edit history, populated by autosave. Absent until the first edit.
  history?: HistoryInfoType;
}

// A blank experience for the "new experience" admin form.
/**
 * A stored experience, with anything added to the type since it was written
 * filled in from the defaults.
 *
 * Every read goes through this rather than casting `snap.data()`, because the
 * cast asserts a shape Firestore does not guarantee: a document written before
 * `freeCancellationDays` existed simply has no such key, and the field is
 * `undefined` however the type is annotated. Which is fine until something does
 * `.toString()` on it, or writes it back into a document — the Admin SDK rejects
 * an undefined value outright.
 *
 * Defaulting here does not quietly rewrite anything. The value only reaches
 * Firestore if somebody edits that field, which is the point at which they have
 * seen it and agreed with it.
 */
export function normaliseExperience(data: Partial<ExperienceType>, id: string): ExperienceType {
  const experience = { ...makeEmptyExperience(), ...data, id };
  // The venue fields replaced `location`, and every experience predating them
  // has a city typed into it and nothing in `venueName`. Reading it forward here
  // keeps the cards and the page header saying what they said yesterday, until
  // somebody types the real venue over it. Nothing is rewritten — the fallback
  // only reaches Firestore if an admin edits that field, having seen it.
  if (!experience.venueName.trim()) experience.venueName = experience.location;
  return experience;
}

export function makeEmptyExperience(): ExperienceType {
  return {
    id: '',
    code: '',
    name: '',
    subtitle: '',
    region: 'kanto',
    venueName: '',
    venueAddress: '',
    venueMapUrl: '',
    location: '',
    layoutFormat: 'standard',
    basePrice: 0,
    bookingType: 'inquiry',
    minGuests: null,
    maxGuests: null,
    commissionEnabled: true,
    commissionPercent: 10,
    // A week. A starting point for a new experience rather than a house rule —
    // the partner terms promise only that the policy "varies by experience and
    // will be communicated at the time of booking".
    freeCancellationDays: 7,
    famEnabled: false,
    optionalServices: {},
    shortDescription: '',
    fullDescription: '',
    includes: [],
    closingStatement: '',
    heroImage: '',
    videoUrl: '',
    galleryImages: [],
    googleMapsEmbedUrl: '',
    supplierId: null,
    closurePeriods: [],
    weekdayClosures: [],
    status: 'active',
    collectionId: null,
    featuredOrder: null,
  };
}
